import { motion, useReducedMotion, type Variants } from 'framer-motion';
import { type ReactNode } from 'react';

import { EASE_OUT_EXPO, REVEAL_TRANSITION } from '@/lib/motion';

export interface RevealProps {
  children: ReactNode;
  className?: string;
  /** Seconds to delay the entrance. */
  delay?: number;
  /** Initial vertical offset in px. */
  y?: number;
}

/**
 * Scroll-reveal wrapper: fades + slides its content up as it enters the
 * viewport, once. Honors `prefers-reduced-motion` (renders statically).
 */
export function Reveal({ children, className, delay = 0, y = 24 }: RevealProps) {
  const reduce = useReducedMotion();
  if (reduce) return <div className={className}>{children}</div>;
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ ...REVEAL_TRANSITION, delay }}
    >
      {children}
    </motion.div>
  );
}

const container: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.04 } },
};
const item: Variants = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: EASE_OUT_EXPO } },
};

export interface StaggerGroupProps {
  children: ReactNode;
  className?: string;
  /** Render as this element instead of `div` — e.g. `ul` when the children
   *  are semantically a list, so the stagger animation doesn't silently
   *  drop list semantics for assistive tech. */
  as?: 'div' | 'ul' | 'dl';
}

/** Container that staggers the entrance of its `StaggerItem` children. */
export function StaggerGroup({ children, className, as: As = 'div' }: StaggerGroupProps) {
  const reduce = useReducedMotion();
  if (reduce) {
    const Static = As;
    return <Static className={className}>{children}</Static>;
  }
  const MotionTag = motion[As];
  return (
    <MotionTag
      className={className}
      variants={container}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: '-80px' }}
    >
      {children}
    </MotionTag>
  );
}

export interface StaggerItemProps {
  children: ReactNode;
  className?: string;
  /** Render as this element instead of `div` — e.g. `li` inside a `ul`
   *  `StaggerGroup`, so the stagger animation doesn't silently drop list
   *  semantics for assistive tech. */
  as?: 'div' | 'li' | 'dd';
}

/** A single staggered child. Must live inside a `StaggerGroup`. */
export function StaggerItem({ children, className, as: As = 'div' }: StaggerItemProps) {
  const reduce = useReducedMotion();
  if (reduce) {
    const Static = As;
    return <Static className={className}>{children}</Static>;
  }
  const MotionTag = motion[As];
  return (
    <MotionTag className={className} variants={item}>
      {children}
    </MotionTag>
  );
}
